import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save, Link as LinkIcon, Megaphone, Instagram, Sparkles, Mail, UserPlus, Plus, X, QrCode, Target } from "lucide-react";

export default function ManageSettings() {
  const [form, setForm] = useState({
    payment_link: "",
    event_title: "",
    event_subtitle: "",
    event_date: "",
    event_location: "",
    event_map_url: "",
    event_description: "",
    announcement_active: false,
    announcement_title: "",
    announcement_body: "",
    instagram_url: "",
    linktree_url: "",
    notification_emails: [],
    subscription_active: false,
    pix_key: "",
    pix_beneficiary_name: "",
    pix_beneficiary_city: "",
  });
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setForm(data);
      } catch {
        toast.error("Erro ao carregar configurações");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="manage-settings">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Configurações</div>
        <h1 className="font-display text-4xl font-bold mt-2">Evento & Pagamento</h1>
        <p className="text-stone-600 mt-2">Informações públicas exibidas na landing page e link de pagamento.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-stone-200 rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-green-700 font-semibold mb-1 flex items-center gap-1.5">
                <QrCode className="h-3.5 w-3.5" /> PIX dinâmico
              </div>
              <h2 className="font-display text-xl font-semibold">Pagamento via PIX (QR code)</h2>
              <p className="text-sm text-stone-500 mt-1">
                Configure sua chave PIX para que carrinhos com <strong>múltiplas unidades ou itens variados</strong> gerem automaticamente um QR code + código Copia-e-Cola com o valor total do pedido. Deixe em branco para desativar.
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="pix-key">Chave PIX</Label>
                <Input
                  id="pix-key"
                  value={form.pix_key || ""}
                  onChange={(e) => setForm({ ...form, pix_key: e.target.value })}
                  placeholder="CPF, e-mail, telefone ou aleatória"
                  data-testid="pix-key-input"
                />
              </div>
              <div>
                <Label htmlFor="pix-name">Nome do beneficiário (máx. 25 caracteres)</Label>
                <Input
                  id="pix-name"
                  value={form.pix_beneficiary_name || ""}
                  onChange={(e) => setForm({ ...form, pix_beneficiary_name: e.target.value.slice(0, 25) })}
                  placeholder="RENASCER MINISTERIO"
                  maxLength={25}
                  data-testid="pix-name-input"
                />
              </div>
              <div>
                <Label htmlFor="pix-city">Cidade do beneficiário (máx. 15, opcional)</Label>
                <Input
                  id="pix-city"
                  value={form.pix_beneficiary_city || ""}
                  onChange={(e) => setForm({ ...form, pix_beneficiary_city: e.target.value.slice(0, 15) })}
                  placeholder="CID OCIDENTAL"
                  maxLength={15}
                  data-testid="pix-city-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold mb-1 flex items-center gap-1.5">
                <LinkIcon className="h-3.5 w-3.5" /> Pagamento
              </div>
              <h2 className="font-display text-xl font-semibold">Link de pagamento (fallback)</h2>
              <p className="text-sm text-stone-500 mt-1">
                Link Stone <strong>usado apenas quando um item não tem link próprio</strong> cadastrado. Cada item deve ter seu próprio link Stone de valor fixo — na tela de checkout, cada unidade do carrinho gera um botão que abre o Stone com valor certo.
              </p>
            </div>
            <div>
              <Label htmlFor="payment-link">URL de pagamento (fallback)</Label>
              <Input
                id="payment-link"
                type="url"
                value={form.payment_link}
                onChange={(e) => setForm({ ...form, payment_link: e.target.value })}
                placeholder="https://pay.stone.com.br/..."
                data-testid="payment-link-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold mb-1">Evento</div>
              <h2 className="font-display text-xl font-semibold">Informações do evento</h2>
              <p className="text-sm text-stone-500 mt-1">Exibidas na landing page.</p>
            </div>
            <div>
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={form.event_title}
                onChange={(e) => setForm({ ...form, event_title: e.target.value })}
                data-testid="event-title-input"
              />
            </div>
            <div>
              <Label htmlFor="subtitle">Subtítulo</Label>
              <Input
                id="subtitle"
                value={form.event_subtitle}
                onChange={(e) => setForm({ ...form, event_subtitle: e.target.value })}
                data-testid="event-subtitle-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Data</Label>
                <Input
                  id="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  placeholder="Ex: 15 a 17 de novembro de 2026"
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <Label htmlFor="location">Local</Label>
                <Input
                  id="location"
                  value={form.event_location}
                  onChange={(e) => setForm({ ...form, event_location: e.target.value })}
                  placeholder="Ex: São Paulo — SP"
                  data-testid="event-location-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="map-url">Link do mapa (Google Maps)</Label>
              <Input
                id="map-url"
                type="url"
                value={form.event_map_url}
                onChange={(e) => setForm({ ...form, event_map_url: e.target.value })}
                placeholder="https://maps.app.goo.gl/..."
                data-testid="event-map-url-input"
              />
            </div>
            <div>
              <Label htmlFor="desc">Descrição</Label>
              <Textarea
                id="desc"
                rows={4}
                value={form.event_description}
                onChange={(e) => setForm({ ...form, event_description: e.target.value })}
                data-testid="event-desc-input"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-stone-200 rounded-2xl" data-testid="announcement-card">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold mb-1 flex items-center gap-1.5">
                <Megaphone className="h-3.5 w-3.5" /> Aviso da landing page
              </div>
              <h2 className="font-display text-xl font-semibold">Comunicado destacado</h2>
              <p className="text-sm text-stone-500 mt-1">
                Exibe uma faixa de aviso no topo da landing page (ex: mudança de horário, informações importantes).
              </p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.announcement_active}
                onChange={(e) => setForm({ ...form, announcement_active: e.target.checked })}
                className="h-4 w-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500"
                data-testid="announcement-active-input"
              />
              <span className="text-sm font-medium">Exibir aviso na landing page</span>
            </label>
            <div>
              <Label htmlFor="ann-title">Título</Label>
              <Input
                id="ann-title"
                value={form.announcement_title || ""}
                onChange={(e) => setForm({ ...form, announcement_title: e.target.value })}
                placeholder="Ex: Inscrições encerradas!"
                data-testid="announcement-title-input"
              />
            </div>
            <div>
              <Label htmlFor="ann-body">Mensagem</Label>
              <Textarea
                id="ann-body"
                rows={3}
                value={form.announcement_body || ""}
                onChange={(e) => setForm({ ...form, announcement_body: e.target.value })}
                placeholder="Texto exibido no aviso."
                data-testid="announcement-body-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 rounded-2xl" data-testid="social-card">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold mb-1 flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Redes sociais
              </div>
              <h2 className="font-display text-xl font-semibold">Links do Renascer</h2>
              <p className="text-sm text-stone-500 mt-1">
                Aparecem como ícones no menu e rodapé da landing page.
              </p>
            </div>
            <div>
              <Label htmlFor="ig" className="flex items-center gap-2"><Instagram className="h-3.5 w-3.5" /> Instagram</Label>
              <Input
                id="ig"
                type="url"
                value={form.instagram_url || ""}
                onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                placeholder="https://www.instagram.com/renascernacional/"
                data-testid="instagram-url-input"
              />
            </div>
            <div>
              <Label htmlFor="lt" className="flex items-center gap-2"><LinkIcon className="h-3.5 w-3.5" /> Linktree</Label>
              <Input
                id="lt"
                type="url"
                value={form.linktree_url || ""}
                onChange={(e) => setForm({ ...form, linktree_url: e.target.value })}
                placeholder="https://linktr.ee/renascernacional"
                data-testid="linktree-url-input"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-stone-200 rounded-2xl" data-testid="notifications-card">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold mb-1 flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> Notificações
              </div>
              <h2 className="font-display text-xl font-semibold">Cópia de cada pedido</h2>
              <p className="text-sm text-stone-500 mt-1">
                Estes emails recebem uma <strong>cópia (cc)</strong> de cada pedido/inscrição confirmada, além do email do próprio comprador.
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const v = emailInput.trim().toLowerCase();
                    if (v && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) && !(form.notification_emails || []).includes(v)) {
                      setForm({ ...form, notification_emails: [...(form.notification_emails || []), v] });
                      setEmailInput("");
                    }
                  }
                }}
                placeholder="equipe@renascer.com"
                data-testid="notification-email-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const v = emailInput.trim().toLowerCase();
                  if (v && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) && !(form.notification_emails || []).includes(v)) {
                    setForm({ ...form, notification_emails: [...(form.notification_emails || []), v] });
                    setEmailInput("");
                  } else {
                    toast.error("Email inválido ou já cadastrado");
                  }
                }}
                data-testid="notification-email-add"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 min-h-[36px]">
              {(form.notification_emails || []).length === 0 ? (
                <span className="text-xs text-stone-400 italic">Nenhum email cadastrado ainda.</span>
              ) : (
                (form.notification_emails || []).map((e) => (
                  <span
                    key={e}
                    data-testid={`notification-email-chip-${e}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-100 border border-stone-200 text-sm text-stone-800"
                  >
                    {e}
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, notification_emails: (form.notification_emails || []).filter((x) => x !== e) })}
                      className="text-stone-500 hover:text-red-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 rounded-2xl" data-testid="subscription-card">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold mb-1 flex items-center gap-1.5">
                <UserPlus className="h-3.5 w-3.5" /> Inscrições presenciais
              </div>
              <h2 className="font-display text-xl font-semibold">Página de inscrição</h2>
              <p className="text-sm text-stone-500 mt-1">
                Habilita a página pública <strong>/inscricao</strong> para o público confirmar presença e opcionalmente comprar itens.
              </p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.subscription_active}
                onChange={(e) => setForm({ ...form, subscription_active: e.target.checked })}
                className="h-4 w-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500"
                data-testid="subscription-active-input"
              />
              <span className="text-sm font-medium">Inscrições abertas ao público</span>
            </label>
            <div className="mt-2 p-3 bg-stone-50 border border-stone-200 rounded-xl text-xs text-stone-600">
              <div className="font-semibold text-stone-800">URL da página:</div>
              <div className="mt-1 font-mono break-all">/inscricao</div>
              <div className="mt-2">
                Coleta: <strong>nome, email, telefone e cidade</strong>. Também mostra os itens da loja como compra opcional.
                Um link <strong>&quot;Inscrever-se&quot;</strong> aparece automaticamente no menu da landing page quando ativado.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-stone-200 rounded-2xl" data-testid="sales-goals-card">
        <CardContent className="p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold mb-1 flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5" /> Metas de vendas
            </div>
            <h2 className="font-display text-xl font-semibold">Barras de progresso do Dashboard</h2>
            <p className="text-sm text-stone-500 mt-1">
              Defina a <strong>meta</strong> (quantidade esperada) para cada tamanho/cardápio. As barras aparecerão automaticamente na aba <strong>Visão geral</strong> ao lado da quantidade vendida. Deixe em branco ou 0 para não exibir a barra.
            </p>
          </div>
          {[
            { key: "camiseta_adulto", name: "Camisa Adulto", labels: ["PP", "P", "M", "G", "GG", "XG", "XGG", "EXG", "EXGG"] },
            { key: "camiseta_infantil", name: "Camisa Infantil", labels: ["1 Ano", "2 Anos", "4 Anos", "6 Anos", "8 Anos", "10 Anos"] },
            { key: "almoco", name: "Almoço", labels: ["Isca de carne", "Strogonoff de frango"] },
            { key: "entrada", name: "Entrada", labels: ["Individual", "Combo"] },
          ].map((cat) => (
            <div key={cat.key} className="rounded-xl border border-stone-200 p-4" data-testid={`goal-cat-${cat.key}`}>
              <div className="text-xs uppercase tracking-widest text-stone-600 font-semibold mb-2">{cat.name}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {cat.labels.map((lbl) => {
                  const catGoals = (form.sales_goals || {})[cat.key] || {};
                  const val = catGoals[lbl] ?? "";
                  return (
                    <div key={lbl} className="flex flex-col">
                      <Label className="text-[10px] uppercase tracking-widest text-stone-500">{lbl}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={val === 0 ? "0" : val || ""}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const num = raw === "" ? "" : Math.max(0, parseInt(raw, 10) || 0);
                          const nextCat = { ...catGoals };
                          if (num === "" || num === 0) {
                            delete nextCat[lbl];
                          } else {
                            nextCat[lbl] = num;
                          }
                          setForm({
                            ...form,
                            sales_goals: { ...(form.sales_goals || {}), [cat.key]: nextCat },
                          });
                        }}
                        placeholder="0"
                        className="mt-1 h-9 text-sm"
                        data-testid={`goal-input-${cat.key}-${lbl}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white rounded-full px-8 py-6"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar alterações
        </Button>
      </div>
    </div>
  );
}

// -- Metas de vendas: definição estática das categorias/tamanhos
// eslint-disable-next-line no-unused-vars
const _GOAL_STRUCTURE = null;